import React, { useCallback, useEffect, useState } from 'react';
import { CreditCard, ExternalLink, Check, XCircle } from 'lucide-react';
import { api } from '../api';
import { deriveBillingView, clampQuantity, defaultQuantity, canCancel, MAX_QUANTITY } from './billingView';
import ConfirmDialog from './ConfirmDialog';
import { detectBrowserCurrencyInfo, pickCurrency, priceFor, PRICES } from './billingCurrency';

const detected = detectBrowserCurrencyInfo();

export default function BillingPage({ userId }) {
  const [view, setView] = useState(null); // null = loading
  const [error, setError] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [busy, setBusy] = useState(false);
  // USD unless the browser looks Australian (AUD) or Japanese (JPY), limited
  // to the currencies the server actually has Chargebee prices for. When the
  // timezone and language signals disagree, a picker lets the user choose.
  const [currency, setCurrency] = useState(detected.currency);
  const [currencies, setCurrencies] = useState(Object.keys(PRICES));
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    try {
      const status = await api.billingStatus(userId);
      setView(deriveBillingView(status));
      const offered = status && Array.isArray(status.currencies) && status.currencies.length
        ? status.currencies.filter((c) => PRICES[c])
        : Object.keys(PRICES);
      setCurrencies(offered);
      setCurrency((c) => pickCurrency(c, offered));
      setError('');
    } catch (e) {
      setError(e.message);
      setView(deriveBillingView(null));
    }
    // Prefill the screen count from the fleet; purely cosmetic, so failures
    // (e.g. no devices yet) silently keep the default of 1.
    try {
      const { devices } = await api.listDevices(userId);
      setQuantity(defaultQuantity(devices));
    } catch (_) { /* keep default */ }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const startCheckout = async () => {
    setBusy(true);
    try {
      const { url } = await api.billingCheckout(clampQuantity(quantity), userId, currency);
      window.location.assign(url); // Chargebee-hosted payment page
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  // Cancels via our own API rather than Chargebee's hosted portal: the portal
  // needs a site setting we don't rely on, and keeping it in-product means we
  // control the wording about what actually happens (access until term end).
  const cancelSubscription = async () => {
    setConfirmCancel(false);
    setBusy(true);
    try {
      const res = await api.billingCancel(userId);
      setNotice(
        res && res.current_term_end
          ? `Cancelled. Your screens keep working until ${new Date(res.current_term_end).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}.`
          : 'Cancelled. Your screens keep working until the end of the current term.'
      );
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const openPortal = async () => {
    setBusy(true);
    try {
      const { url } = await api.billingPortal(userId);
      window.location.assign(url); // Chargebee self-serve portal
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  if (view === null) return <div className="empty">Loading billing…</div>;

  return (
    <>
      <div className="page-head">
        <h2><CreditCard size={20} /> Billing</h2>
      </div>

      {error && <p className="error-text">{error}</p>}

      {view.mode === 'disabled' && (
        <div className="card card-pad">
          <h3>Free while in alpha</h3>
          <p className="muted-text">
            You're on the Free plan. Paid plans ({priceFor(currency).screen}/screen/month, removes the
            FreeTV watermark) aren't switched on yet — everything is free during the alpha.
          </p>
        </div>
      )}

      {view.mode === 'free' && (
        <div className="billing-grid">
          <div className="card card-pad">
            <h3>Free <span className="plan-pill current">Current plan</span></h3>
            <p className="plan-price">{priceFor(currency).free}<span> /month</span></p>
            <ul className="plan-list">
              <li><Check size={14} /> 1 screen</li>
              <li><Check size={14} /> All templates &amp; the full API</li>
              <li><Check size={14} /> Shows a small FreeTV watermark</li>
            </ul>
          </div>

          <div className="card card-pad">
            <h3>Business</h3>
            <p className="plan-price">{priceFor(currency).screen}<span> /screen /month</span></p>
            {detected.ambiguous && currencies.length > 1 && (
              <div className="field">
                <label className="label" htmlFor="billing-currency">Billing currency</label>
                <select id="billing-currency" className="input" value={currency}
                  onChange={(e) => setCurrency(e.target.value)}>
                  {currencies.map((c) => (
                    <option key={c} value={c}>{c} — {priceFor(c).screen}/screen/month</option>
                  ))}
                </select>
              </div>
            )}
            <ul className="plan-list">
              <li><Check size={14} /> No watermark</li>
              <li><Check size={14} /> As many screens as you need</li>
              <li><Check size={14} /> Priority support</li>
            </ul>
            <div className="field">
              <label className="label" htmlFor="billing-qty">Screens</label>
              <input id="billing-qty" className="input" type="number" min="1" max={MAX_QUANTITY}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                onBlur={() => setQuantity(clampQuantity(quantity))} />
            </div>
            <button className="btn btn-primary" disabled={busy} onClick={startCheckout}>
              Upgrade — checkout <ExternalLink size={14} />
            </button>
            <p className="muted-text small">Payment is handled securely by Chargebee.</p>
          </div>
        </div>
      )}

      {view.mode === 'business' && (
        <div className="card card-pad">
          <h3>Business <span className="plan-pill current">Current plan</span></h3>
          <p className="muted-text">
            {view.quantity} screen{view.quantity === 1 ? '' : 's'} · {priceFor(view.currency || currency).screen}/screen/month
            {view.termEnd && (
              view.endsInsteadOfRenews
                ? <> · ends {view.termEnd} (cancelled — active until then)</>
                : <> · renews {view.termEnd}</>
            )}
          </p>
          {notice && <p className="muted-text small" style={{ color: 'var(--ok)' }}>{notice}</p>}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button className="btn" disabled={busy} onClick={openPortal}>
              Manage subscription <ExternalLink size={14} />
            </button>
            {canCancel(view) && (
              <button className="btn btn-danger" disabled={busy} onClick={() => setConfirmCancel(true)}>
                <XCircle size={15} /> Cancel subscription
              </button>
            )}
          </div>
          <p className="muted-text small">
            Cancelling stops the next renewal — your screens keep working until the end of
            the current term. Payment method and invoices live in the Chargebee portal.
          </p>
        </div>
      )}

      <ConfirmDialog
        open={confirmCancel}
        danger
        title="Cancel your subscription?"
        body={
          view && view.termEnd
            ? `Your ${view.quantity} screen${view.quantity === 1 ? '' : 's'} keep working until ${view.termEnd}, then drop to the free tier (1 screen, FreeTV watermark). You won't be charged again.`
            : "Your screens keep working until the end of the current term, then drop to the free tier (1 screen, FreeTV watermark). You won't be charged again."
        }
        confirmLabel="Cancel subscription"
        onConfirm={cancelSubscription}
        onCancel={() => setConfirmCancel(false)}
      />
    </>
  );
}
